import type { QueryClient, QueryKey } from '@tanstack/vue-query';
import { TypeQueryKey } from '@/modules/common/enums/query-key.enum.ts';
import { DOCUMENT_HEADER_COLOR_KEY } from '@/modules/document/consts/document-color.ts';
import { nextTick, unref } from 'vue';
import {
  acceptNextSameIdUpdate,
  clearAcceptedSameIdUpdate,
} from '@/modules/viewer/composables/use-viewer-selection.ts';

const CHANNEL_NAME = 'alphadoc-document-updates';
const STORAGE_KEY = 'alphadoc:document-update';

const VIEWER_BASE_KEYS = new Set(['document', 'controls', 'assignments', 'assignments-search']);

const COLOR_BASE_KEYS = new Set(['document', 'controls', 'assignments']);

export interface DocumentUpdateEvent {
  type: 'document-updated';
  documentId: number;
  timestamp: number;
  eventId: string;
}

interface ActiveDocumentContext {
  scope: string | null;
  rowId: string | null;
  refresh: () => Promise<unknown> | unknown;
}

interface DocumentUpdateSyncOptions {
  getActiveDocumentContext?: (documentId: number) => ActiveDocumentContext | null;
}

let channel: BroadcastChannel | null = null;

function getChannel() {
  if (typeof BroadcastChannel !== 'function') {
    return null;
  }

  try {
    channel ??= new BroadcastChannel(CHANNEL_NAME);
    return channel;
  } catch {
    return null;
  }
}

function isDocumentUpdateEvent(value: unknown): value is DocumentUpdateEvent {
  if (!value || typeof value !== 'object') {
    return false;
  }

  const event = value as Partial<DocumentUpdateEvent>;

  return event.type === 'document-updated' && typeof event.documentId === 'number';
}

function matchesDocumentUpdate(queryKey: QueryKey, documentId: number) {
  if (queryKey[0] === DOCUMENT_HEADER_COLOR_KEY) {
    return queryKey[2] === documentId;
  }

  const [, baseKey, typeKey, entityId] = queryKey;

  if (typeKey === TypeQueryKey.Viewer || typeKey === TypeQueryKey.Count) {
    return typeof baseKey === 'string' && VIEWER_BASE_KEYS.has(baseKey);
  }

  if (typeKey !== TypeQueryKey.Color || typeof baseKey !== 'string' || !COLOR_BASE_KEYS.has(baseKey)) {
    return false;
  }

  return entityId === undefined || unref(entityId) === documentId;
}

function subscribe(listener: (event: DocumentUpdateEvent) => void) {
  const broadcastChannel = getChannel();

  if (broadcastChannel) {
    const onMessage = ({ data }: MessageEvent) => {
      if (isDocumentUpdateEvent(data)) {
        listener(data);
      }
    };
    broadcastChannel.addEventListener('message', onMessage);

    return () => {
      broadcastChannel.removeEventListener('message', onMessage);
      broadcastChannel.close();
      if (channel === broadcastChannel) {
        channel = null;
      }
    };
  }

  if (typeof window === 'undefined') {
    return () => undefined;
  }

  const onStorage = ({ key, newValue }: StorageEvent) => {
    if (key !== STORAGE_KEY || !newValue) {
      return;
    }

    try {
      const event: unknown = JSON.parse(newValue);
      if (isDocumentUpdateEvent(event)) {
        listener(event);
      }
    } catch {
      // Ignore malformed values written by older clients or browser extensions.
    }
  };

  window.addEventListener('storage', onStorage);

  return () => window.removeEventListener('storage', onStorage);
}

export function publishDocumentUpdated(documentId: number) {
  const event: DocumentUpdateEvent = {
    type: 'document-updated',
    documentId,
    timestamp: Date.now(),
    eventId: `${Date.now()}-${Math.random()}`,
  };

  const broadcastChannel = getChannel();

  if (broadcastChannel) {
    broadcastChannel.postMessage(event);
    return;
  }

  try {
    if (typeof localStorage === 'undefined') {
      return;
    }

    localStorage.setItem(STORAGE_KEY, JSON.stringify(event));
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Cross-tab sync is an enhancement; saving the document must still succeed if storage is unavailable.
  }
}

export function setupDocumentUpdateSync(queryClient: QueryClient, options: DocumentUpdateSyncOptions = {}) {
  return subscribe(({ documentId }) => {
    const context = options.getActiveDocumentContext?.(documentId) ?? null;
    const accepted = context?.scope && context.rowId ? { scope: context.scope, rowId: context.rowId } : null;

    if (accepted) {
      acceptNextSameIdUpdate(accepted.scope, accepted.rowId);
    }

    const tasks: Promise<unknown>[] = [
      queryClient.invalidateQueries({
        predicate: ({ queryKey }) => matchesDocumentUpdate(queryKey, documentId),
      }),
    ];

    if (context) {
      tasks.push(Promise.resolve().then(context.refresh));
    }

    void Promise.allSettled(tasks)
      .then(() => nextTick())
      .finally(() => {
        if (accepted) {
          clearAcceptedSameIdUpdate(accepted.scope, accepted.rowId);
        }
      });
  });
}
